import type { Editor } from '@tiptap/core';
import { FC, MutableRefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionSubmit,
	ActionText,
	AmountButton,
	GiverToggle,
	useActionInput,
} from '@bonusly/common-action-input';
import { CommonCard } from '@bonusly/common-give-box';
import { Box, Flex, Hide, rem, Spacer, StyleProps } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useCurrentUser, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { useBizyContextualSlash } from '../../../../bizy-side-panel/hooks/use-bizy-contextual-slash';
import { useBizyActiveComposersStore } from '../../../../bizy-side-panel/store/use-bizy-active-composers-store';
import { UserAvatar } from '../../components/user-avatar';
import { CommentEvent } from '../../events';
import { useCommentInput } from './hooks/use-comment-input';
import { SuggestedComments } from './suggested-comments';

export interface CommentComposerProps extends StyleProps {
	id?: string;
	bonus: Bonus;
	initalText?: string;
	onSuccess?: VoidFunction;
	commentBoxRef?: MutableRefObject<HTMLDivElement | null>;
}

const DynamicStyledActionText = () => {
	const { attachment, isLoadingAttachment } = useActionInput();
	return <ActionText inputStyles={{ minHeight: attachment || isLoadingAttachment ? undefined : rem(150) }} />;
};

export const CommentComposer: FC<CommentComposerProps> = ({ id, bonus, initalText, onSuccess, commentBoxRef }) => {
	const { t } = useTranslation('recognition');
	const user = useCurrentUser();
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const dispatch = useDispatch();
	const { isOwnRecognition, onSuggestionSelected, onSuggestionCleared, ...config } = useCommentInput(bonus, {
		onSuccess,
	});

	// HABITS-1294: intercept `/` at the start of a comment to open Bizy
	// contextually with the parent post's context so users can draft a reply
	// without leaving the thread. When the current user authored the post,
	// we flip the framing so Bizy doesn't suggest "thanking yourself" — the
	// user is most likely following up with the original receivers.
	const author = bonus.giver;
	const currentUserIsAuthor = !!author?.id && author.id === user.id;
	const receivers = bonus.receivers ?? [];
	// Names sent to Bizy for ADDRESSING (i.e. "Thanks Sid," or "Sid,
	// you handled that so well"). Always prefer `preferred_first_name`
	// over `first_name` over `display_name` — the legal full name is
	// usually wrong here. Bonusly users set `preferred_first_name` to
	// "Sid" / "Maddy" / etc.; using "Sidney Bynum" makes Bizy comments
	// stand out as bot-written.
	const preferredFirstNameOf = (u: { preferred_first_name?: string; first_name?: string; display_name?: string } | undefined): string | undefined =>
		u?.preferred_first_name?.trim() || u?.first_name?.trim() || u?.display_name?.trim() || undefined;
	const receiverFirstNames = receivers.map((r) => preferredFirstNameOf(r)).filter((n): n is string => !!n);
	const formatPeopleList = (names: string[]): string | undefined => {
		if (names.length === 0) return undefined;
		if (names.length === 1) return names[0];
		if (names.length === 2) return `${names[0]} and ${names[1]}`;
		return `${names[0]} and ${names.length - 1} others`;
	};
	const authorFirstName = preferredFirstNameOf(author);
	// `recipientLabel` drives the Bizy starter sentence ("I want to comment
	// on Sam's post — help me figure out what to add."), so use the
	// first-name version too for consistency with the addressing names.
	const recipientLabel = currentUserIsAuthor ? formatPeopleList(receiverFirstNames) : authorFirstName;
	const defaultPlaceholder = isOwnRecognition ? t('comment.input_thanks_placeholder') : t('comment.input_placeholder');
	const focusedPlaceholder = config.placeholder ?? defaultPlaceholder;
	const placeholder = useCallback(
		({ editor }: { editor: Editor }) => (editor.isFocused ? focusedPlaceholder : defaultPlaceholder),
		[defaultPlaceholder, focusedPlaceholder]
	);

	// Bring the composer back into view when Bizy hands a draft back. The
	// composer might be off-screen (user scrolled while chatting with Bizy
	// in the side panel), so scroll the container into view before / after
	// inserting and focus the editor.
	const bringComposerIntoFocus = () => {
		const handle = config.ref.current;
		if (!handle) return;
		commentBoxRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		handle.focus();
	};

	// HABITS-1294: register this composer in the global active-composers
	// store so the Bizy UI block dispatcher can route Insert / Send now
	// actions to the right composer (keyed by bonusId — multiple
	// composers can be open across the feed). The handle is stable
	// across re-renders because it reads from refs at call time, so we
	// only need to re-register if the bonus id itself changes.
	const registerCommentComposer = useBizyActiveComposersStore((state) => state.registerCommentComposer);
	useEffect(() => {
		const insertText = (text: string) => {
			dispatch(CommentEvent.Open, { id: bonus.id });
			const apply = () => {
				const live = config.ref.current;
				if (!live) return;
				live.insertWithHighlight(text);
				bringComposerIntoFocus();
			};
			if (config.ref.current) {
				apply();
			} else {
				requestAnimationFrame(() => requestAnimationFrame(apply));
			}
		};
		const submitNow = (text: string): boolean => {
			dispatch(CommentEvent.Open, { id: bonus.id });
			const submit = () => {
				const live = config.ref.current;
				if (!live) return false;
				live.reset();
				// `insertWithHighlight` lets the user see what's about to
				// be posted (the editor flashes the AI-touched highlight
				// for a beat before the comment is created and the editor
				// resets). This also matches the "Insert draft" visual so
				// the actions look related.
				live.insertWithHighlight(text);
				// Two rAFs: TipTap insertions schedule a transaction;
				// reading `data()` on the same frame can return the
				// pre-insert state. Two frames is enough for the editor
				// to commit the transaction AND for the derived data hook
				// to recompute.
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const data = live.data();
						if (data && data.text.trim().length > 0) {
							config.onSubmit(data);
						}
					});
				});
				return true;
			};
			if (config.ref.current) return submit();
			requestAnimationFrame(() => requestAnimationFrame(submit));
			return true;
		};
		return registerCommentComposer(bonus.id, {
			insertDraft: insertText,
			sendNow: submitNow,
			scrollIntoViewAndFocus: bringComposerIntoFocus,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- handle reads from refs; we only re-register when bonus.id changes
	}, [bonus.id, registerCommentComposer]);

	const { handleKeyDownCapture: handleBizySlashKeyDown } = useBizyContextualSlash({
		surface: 'comment',
		enabled: isBizyEnabledForUser,
		getInputHandle: () => config.ref.current,
		getSurfaceData: () => ({
			// Key name matches the backend's
			// `context[:bizy_contextual_parentBonusId]` reader and the
			// frontend action schema's `parentBonusId` field — the
			// dispatcher uses it to route the action to the right
			// composer in the registry.
			parentBonusId: bonus.id,
			parentAuthorId: author?.id ?? null,
			// `parentAuthorName` is the formal display name (kept for any
			// fallback path that needs the full version); `parentAuthorFirstName`
			// is the addressable name Bizy uses for "Thanks Sid," / "Sid,
			// this means a lot...". The addressable version is what any
			// surface-section / addressing-section instruction reads.
			parentAuthorName: author?.display_name ?? null,
			parentAuthorFirstName: authorFirstName ?? null,
			parentReason: bonus.reason ?? null,
			// `currentUserIsAuthor` tells Bizy the user wrote the post they're
			// commenting on (so it shouldn't suggest thanking the author —
			// that's the user themselves). We also forward the receiver
			// names so Bizy can naturally follow up with them.
			currentUserIsAuthor,
			receiverIds: receivers.map((r) => r.id),
			// `receiverNames` here is the addressing version (preferred
			// first name) — that's what every prompt branch reads as
			// `bizy_contextual_receiverNames`. The full display name
			// isn't useful for addressing.
			receiverNames: receiverFirstNames,
			// Pre-existing flag from `useSayThanks(bonus)`: true when the
			// current user is a *receiver* of the recognition (and would
			// typically be saying thanks). Distinct from `currentUserIsAuthor`.
			isOwnRecognition,
		}),
		getRecipientLabel: () => recipientLabel,
		// `insert` / `sendNow` are no longer threaded through the slash
		// hook — actions on a comment_draft_card include the
		// parentBonusId, and the dispatcher looks the composer up in
		// `useBizyActiveComposersStore` (registered by the effect above).
		// That makes clicks idempotent: the registry is alive as long as
		// the composer is mounted, so the user can click Insert / Send
		// now multiple times without the second click falling back to
		// the give-box.
		onDismiss: () => {
			// Bizy panel closed without insert (or after insert finished). Make
			// sure the user can see and act on the composer.
			bringComposerIntoFocus();
		},
	});

	return (
		<CommonCard id={id} variant="inlay" bg="bg.subtle" ref={commentBoxRef}>
			<Flex columnGap={2}>
				<Hide below="sm">
					<Box>
						<UserAvatar size="md" user={user} />
					</Box>
				</Hide>

				<Box backgroundColor="bg.surface" borderRadius="md" boxShadow="sm" p={3} w="full" minW={0}>
					<ActionInput autofocus="end" content={initalText} {...config} placeholder={placeholder}>
						<Flex alignItems="center" columnGap={2}>
							<Box>
								<AmountButton />
							</Box>
							<GiverToggle />
						</Flex>

						<Box mb={2} position="relative" onKeyDownCapture={handleBizySlashKeyDown}>
							<DynamicStyledActionText />
							{isOwnRecognition && (
								<Box position="absolute" bottom={0} left={0} right={0} zIndex={1}>
									<SuggestedComments bonus={bonus} onSetText={onSuggestionSelected} onSuggestionCleared={onSuggestionCleared} />
								</Box>
							)}
							<ActionFeedback />
						</Box>

						<ActionMedia w="full" />

						<Flex
							alignItems={{ base: 'stretch', xs: 'flex-start' }}
							direction={{ base: 'column', xs: 'row' }}
							gap={2}
							id="comment-composer-submit-container"
							justifyContent="space-between"
							mt={3}
							w="full"
						>
							<ActionDecorations />
							<Spacer />

							<ActionSubmit size="md" />
						</Flex>
					</ActionInput>
				</Box>
			</Flex>
		</CommonCard>
	);
};
